import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { get, post, type ApiData } from '@app/api/typed';
import type { components } from '@app/api/types';

/**
 * Discovery — as duas operações do botão «Discovery agora», compartilhadas por
 * T3 (Visão geral), T4 (Identidades), T21 (Configurações do tenant) e pela
 * moldura do console.
 *
 * **Assíncrono desde 29/08.** Antes o `POST` esperava a varredura inteira: no lab
 * ela passou de 20 s, o axios abortou, o backend caiu junto (`context canceled`) e
 * a tela anunciou «Nada foi gravado» sobre 5 das 21 identidades que tinham sido
 * gravadas. Aumentar o timeout do cliente só empurrou o problema para 120 s.
 *
 * Agora o `POST` **pede um tique** e responde `202`; quem conta o resto é o
 * `GET /discovery/status`, consultado a cada 3 s até `running: false`.
 */

export type DiscoveryStatus = ApiData<'/api/v1/discovery/status'>;
export type DiscoveryStarted = ApiData<'/api/v1/discovery/run', 'post'>;
export type DiscoveryResult = components['schemas']['DiscoveryResult'];

export const discoveryKeys = {
  status: ['discovery', 'status'] as const,
};

/**
 * De quanto em quanto tempo se pergunta pelo estado da varredura.
 *
 * Três segundos: a varredura real leva 20–40 s, então são ~10 perguntas por
 * execução — barato o bastante para não pesar e curto o bastante para o fim não
 * parecer atraso. Só corre **enquanto há varredura**: parada, a consulta não se
 * repete (ver `refetchInterval` abaixo).
 */
export const DISCOVERY_POLL_MS = 3_000;

/**
 * De quanto em quanto tempo perguntar de novo, dado o último estado conhecido.
 *
 * O polling é ligado pelo **próprio dado**: correndo, repete a cada
 * `DISCOVERY_POLL_MS`; parado, não repete nada. Um intervalo fixo perguntaria o
 * dia inteiro por uma resposta que quase sempre é "parado" — e o console abre em
 * telas que não têm nada a ver com discovery.
 */
export function statusInterval(status?: DiscoveryStatus): number | false {
  return status?.running ? DISCOVERY_POLL_MS : false;
}

/**
 * Estado da varredura (29/08). Consultado ao abrir qualquer tela do console: é
 * assim que uma varredura iniciada por **outra pessoa** — ou pelo periódico —
 * aparece no indicador de tarefas de quem acabou de chegar.
 */
export function useDiscoveryStatus(): UseQueryResult<DiscoveryStatus, ApiError> {
  return useQuery<DiscoveryStatus, ApiError>({
    queryKey: discoveryKeys.status,
    queryFn: () => get('/api/v1/discovery/status'),
    // Estado vivo: nunca servir do cache sem confirmar.
    staleTime: 0,
    refetchInterval: (query) => statusInterval(query.state.data),
    // Continua perguntando com a aba em segundo plano: quem clicou e foi ver
    // outra coisa volta e encontra a tarefa fechada, não um indicador preso.
    refetchIntervalInBackground: true,
  });
}

/**
 * **Discovery agora** (FR-039, FR-040) — dispara e devolve na hora (202).
 *
 * Sem `timeoutMs`: a chamada agora é curta de verdade, e o teto de 2 minutos que
 * existia aqui era a cicatriz da versão síncrona.
 *
 * `onSuccess` faz duas coisas, e as duas importam:
 *
 *   - grava o `202` no cache do status, para o indicador aparecer **no clique** e
 *     não até 3 s depois — nesse intervalo o botão pareceria não ter feito nada;
 *   - pede uma confirmação imediata ao servidor. É ela que fecha o caso da
 *     varredura curta: contra um registry pequeno a varredura pode acabar antes
 *     do primeiro intervalo, e sem esta consulta o indicador ficaria três segundos
 *     anunciando uma tarefa que já terminou.
 */
export function useTriggerDiscovery(): UseMutationResult<DiscoveryStarted, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation<DiscoveryStarted, ApiError, void>({
    mutationFn: () => post('/api/v1/discovery/run'),
    onSuccess: (iniciada) => {
      queryClient.setQueryData<DiscoveryStatus>(discoveryKeys.status, (previous) => ({
        ...previous,
        running: true,
        started_at: iniciada.started_at,
        started_by: iniciada.started_by,
        // `ultima_execucao_em`/`ultimo_resultado` continuam sendo os da varredura
        // **anterior** — a nova ainda não terminou, e inventá-los aqui é o que
        // fazia a tela falar sobre uma execução que não existiu.
        last_execution_at: previous?.last_execution_at ?? null,
        last_result: previous?.last_result ?? null,
      }));
      void queryClient.invalidateQueries({ queryKey: discoveryKeys.status });
    },
  });
}
